// Pure logic for the Field Safety page (SafetyDashboard / WorkerRoster /
// SOSAlertCard). Kept out of the components so it can be unit-tested — the
// chatListModel / incidentModel convention.

/** `User #69acf847` — never the full UUID (MyCRM 232). */
export function shortUserLabel(userId, userLabel = 'User') {
  return `${userLabel} #${String(userId ?? '').slice(0, 8)}`;
}

/**
 * Canonical "open a chat with this person" deep link — ChatPage handles
 * `?user=` (see src/CLAUDE.md). Returns null when there is nobody to message.
 */
export function chatPathForUser(userId) {
  if (!userId) return null;
  return `/chat?user=${encodeURIComponent(String(userId))}`;
}

/**
 * Build the Worker Status roster from the three signals the page has:
 * WebSocket presence (online users, keyed by user id), active SOS alerts and
 * missed check-in events. Ids are trackizy_users UUIDs and are compared as
 * strings — the old `Number(userId)` coercion was always NaN, so an online
 * worker with an active SOS was never flagged. Names resolve from presence,
 * then the alert / event's `user_name` (joined server-side), then the short
 * id label.
 */
export function buildWorkerRows({ onlineUsers = {}, activeAlerts = [], missedEvents = [], userLabel = 'User' }) {
  const rows = new Map();
  const ensure = (userId) => {
    const key = String(userId);
    if (!rows.has(key)) {
      rows.set(key, { userId: key, name: null, isOnline: false, hasSOS: false, missedCheckin: false });
    }
    return rows.get(key);
  };
  const nameFrom = (row, candidate) => {
    if (!row.name && typeof candidate === 'string' && candidate.trim()) row.name = candidate.trim();
  };

  Object.entries(onlineUsers).forEach(([userId, presence]) => {
    const row = ensure(userId);
    row.isOnline = true;
    nameFrom(row, presence?.name);
  });
  activeAlerts.forEach((alert) => {
    if (!alert?.user_id) return;
    const row = ensure(alert.user_id);
    row.hasSOS = true;
    nameFrom(row, alert.user_name);
  });
  missedEvents.forEach((event) => {
    if (!event?.user_id) return;
    const row = ensure(event.user_id);
    row.missedCheckin = true;
    nameFrom(row, event.user_name);
  });

  return Array.from(rows.values())
    .map((row) => ({ ...row, name: row.name ?? shortUserLabel(row.userId, userLabel) }))
    .sort((a, b) => {
      // SOS first, then missed check-ins, then online, then by name.
      if (a.hasSOS !== b.hasSOS) return a.hasSOS ? -1 : 1;
      if (a.missedCheckin !== b.missedCheckin) return a.missedCheckin ? -1 : 1;
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
}

/**
 * Apply one pushed Safety event to the active-alert list (useSOS).
 * `sos` adds a new alert once; `sos_location` replaces the alert in place when a
 * late GPS fix arrives for an SOS already shown (combined app release spec §5);
 * `sos_ack` removes it. Anything else returns `prev` unchanged.
 */
export function applySosEvent(prev, data) {
  const alert = data?.alert;
  if (!alert?.id) return prev;
  if (data.type === 'sos') {
    return prev.some((a) => a.id === alert.id) ? prev : [alert, ...prev];
  }
  if (data.type === 'sos_location') {
    return prev.some((a) => a.id === alert.id) ? prev.map((a) => (a.id === alert.id ? { ...a, ...alert } : a)) : prev;
  }
  if (data.type === 'sos_ack') {
    return prev.filter((a) => a.id !== alert.id);
  }
  return prev;
}
